import {
  ConnectionState,
  type ParticipantPropertiesPayload,
  event_peer_share_state_change,
  type event_peer_video_state_change,
} from '@zoom/videosdk';

type ZoomRemoteParticipantEvent =
  | 'user-updated'
  | 'peer-video-state-change'
  | 'command-channel-status'
  | 'peer-share-state-change';

export type VideoStateChangePayload = Parameters<typeof event_peer_video_state_change>[0];

export type PeerShareStateChangePayload = Parameters<typeof event_peer_share_state_change>[0];

// Map each Zoom event to its payload type
type ZoomEventPayloadMap = {
  'user-updated': ParticipantPropertiesPayload[];
  'peer-video-state-change': VideoStateChangePayload;
  'command-channel-status': ConnectionState;
  'peer-share-state-change': PeerShareStateChangePayload;
};

// Map each Zoom event to whether it needs participantUserId
type ZoomEventRequiresParticipantId = {
  'user-updated': true;
  'peer-video-state-change': true;
  'command-channel-status': false;
  'peer-share-state-change': true;
};

// Clean predicate function type that adapts based on the event
type PredicateFunction<T extends ZoomRemoteParticipantEvent> = ZoomEventRequiresParticipantId[T] extends true
  ? (args: ZoomEventPayloadMap[T], participantUserId: number) => boolean
  : (args: ZoomEventPayloadMap[T]) => boolean;

export type ZoomEventTrigger<T extends ZoomRemoteParticipantEvent> = {
  zoomEvent: T;
  predicate: PredicateFunction<T>;
};

export function checkUserAudioSubscribed(payload: ParticipantPropertiesPayload[], participantUserId: number): boolean {
  return payload.some(
    (participant) =>
      participant.userId === participantUserId && (participant.audio === 'computer' || participant.audio === 'phone'),
  );
}

export function checkUserAudioUnsubscribed(
  payload: ParticipantPropertiesPayload[],
  participantUserId: number,
): boolean {
  return payload.some((participant) => participant.userId === participantUserId && participant.muted === true);
}

export function checkUserAudioEnabled(payload: ParticipantPropertiesPayload[], participantUserId: number): boolean {
  return payload.some((participant) => participant.userId === participantUserId && participant.muted === false);
}

export function checkUserVideoEnabled(payload: ParticipantPropertiesPayload[], participantUserId: number): boolean {
  return payload.some((participant) => participant.userId === participantUserId && participant.bVideoOn === true);
}

export function checkVideoSubscribed(payload: VideoStateChangePayload, participantUserId: number): boolean {
  return payload.userId === participantUserId && payload.action === 'Start';
}

export function checkVideoUnsubscribed(payload: VideoStateChangePayload, participantUserId: number): boolean {
  return payload.userId === participantUserId && payload.action === 'Stop';
}

export function checkCommandChannelState(payload: ConnectionState, targetState: ConnectionState): boolean {
  return payload === targetState;
}

export function checkScreenShareSubscribed(payload: PeerShareStateChangePayload, participantUserId: number): boolean {
  return payload.userId === participantUserId && payload.action === 'Start';
}

export function checkScreenShareUnsubscribed(payload: PeerShareStateChangePayload, participantUserId: number): boolean {
  return payload.userId === participantUserId && payload.action === 'Stop';
}
